(() => {
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const init = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const controller = new AbortController();
    const listen = (target, type, callback, options = {}) => target?.addEventListener(type, callback, { ...options, signal: controller.signal });
    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => [...root.querySelectorAll(selector)];
    const variants = JSON.parse(q('[data-product-variants]').textContent);
    const soldOut = root.dataset.soldOut === 'true';
    const photos = JSON.parse(q('[data-product-photos]').textContent);
    const size = q('[data-product-size]');
    const picker = q('[data-size-picker]');
    const menu = q('[data-size-menu]');
    const backdrop = q('[data-size-backdrop]');
    const sizes = qa('[data-size-option]');
    const colorGroup = q('[data-product-option]');
    const colors = qa('[data-option-value]');
    const add = q('[data-product-add]');
    const error = q('[data-product-error]');
    let selectedColor = colors.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.optionValue || colorGroup?.dataset.optionSelected;
    let selectedSize = null;
    let busy = false;
    const sizeIndex = size ? Number(size.dataset.optionPosition) - 1 : -1;
    const colorIndex = colorGroup ? Number(colorGroup.dataset.optionPosition) - 1 : -1;
    const colorMatches = (variant) => colorIndex < 0 || variant.options[colorIndex] === selectedColor;
    const selectedVariant = () => variants.find((variant) => colorMatches(variant) && (sizeIndex < 0 || variant.options[sizeIndex] === selectedSize));
    const closeSize = (restoreFocus = false) => {
      if (!size) return;
      size.setAttribute('aria-expanded', 'false');
      menu.hidden = true;
      backdrop.hidden = true;
      root.classList.remove('is-selecting');
      if (restoreFocus) size.focus();
    };
    const openSize = (withBackdrop = false) => {
      if (!size || busy) return;
      size.setAttribute('aria-expanded', 'true');
      menu.hidden = false;
      if (withBackdrop) {
        backdrop.hidden = false;
        root.classList.add('is-selecting');
      }
    };
    const sync = () => {
      sizes.forEach((button) => {
        button.disabled = !variants.some((variant) => colorMatches(variant) && (soldOut || variant.available) && variant.options[sizeIndex] === button.dataset.sizeOption);
        button.setAttribute('aria-selected', String(selectedSize === button.dataset.sizeOption));
      });
      const variant = selectedVariant() || variants.find((item) => colorMatches(item) && item.available) || variants.find(colorMatches);
      // Linked-product colors must never silently add a different legacy color.
      if (add) add.disabled = busy || !variants.some((item) => colorMatches(item) && item.available);
      if (!variant) return;
      q('[data-product-price]').textContent = variant.price;
      q('[data-product-compare-price]').textContent = variant.compareAt || '';
      q('[data-product-compare-price]').hidden = !variant.compareAt;
      if (q('[data-add-price]')) q('[data-add-price]').textContent = variant.price;
      if (q('[data-product-variant-id]')) q('[data-product-variant-id]').value = variant.id;
      if (q('[data-color-current]')) q('[data-color-current]').textContent = selectedColor;
    };
    colors.forEach((button) => listen(button, 'click', () => {
      selectedColor = button.dataset.optionValue;
      if (selectedSize && !variants.some((variant) => colorMatches(variant) && variant.options[sizeIndex] === selectedSize && (soldOut || variant.available))) {
        selectedSize = null;
        renderSize();
      }
      colors.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      closeSize();
      sync();
    }));
    const renderSize = () => {
      if (!size) return;
      const current = q('[data-size-current]');
      current.replaceChildren();
      const desktop = document.createElement('span');
      desktop.className = 'tsyrk-size-desktop';
      desktop.textContent = selectedSize ? 'Size ' + selectedSize : 'Select size';
      const mobile = document.createElement('span');
      mobile.className = 'tsyrk-size-mobile';
      mobile.textContent = selectedSize || 'Size';
      current.append(desktop, mobile);
    };
    sizes.forEach((button) => listen(button, 'click', () => {
      selectedSize = button.dataset.sizeOption;
      renderSize();
      sync();
      const variant = selectedVariant();
      if (variant) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
      closeSize(true);
    }));
    listen(size, 'click', () => size.getAttribute('aria-expanded') === 'true' ? closeSize() : openSize());
    listen(picker, 'pointerenter', (event) => { if (event.pointerType === 'mouse' && innerWidth > 1024) openSize(); });
    listen(picker, 'pointerleave', () => { if (backdrop.hidden && !picker.contains(document.activeElement)) closeSize(); });
    listen(backdrop, 'click', () => closeSize(true));
    listen(document, 'click', (event) => { if (picker && !picker.contains(event.target) && !add?.contains(event.target)) closeSize(); });
    listen(picker, 'keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); closeSize(true); }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openSize();
        const available = sizes.filter((button) => !button.disabled);
        const index = available.indexOf(document.activeElement);
        const next = index < 0 ? (event.key === 'ArrowDown' ? 0 : available.length - 1) : (index + (event.key === 'ArrowDown' ? 1 : -1) + available.length) % available.length;
        available[next]?.focus();
      }
    });
    listen(picker, 'focusout', (event) => { if (!picker.contains(event.relatedTarget) && backdrop.hidden) closeSize(); });
    const tabs = qa('[data-product-tab]');
    const activate = (tab, scroll = false) => {
      if (!tab) return;
      tabs.forEach((item) => { item.setAttribute('aria-selected', String(item === tab)); item.tabIndex = item === tab ? 0 : -1; });
      qa('[data-product-panel]').forEach((panel) => { panel.hidden = panel.dataset.productPanel !== tab.dataset.productTab; });
      const tabList = tab.parentElement;
      tabList.scrollTo({ left: Math.max(0, tab.offsetLeft - tabList.offsetLeft - 8), behavior: motion() });
      if (scroll) {
        const summary = q('[data-product-summary]');
        const headerOffset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tsyrk-sticky-header-height')) || 0;
        window.scrollTo({ top: summary.getBoundingClientRect().top + scrollY - headerOffset - 24, behavior: motion() });
      }
    };
    tabs.forEach((tab, index) => {
      listen(tab, 'click', () => activate(tab));
      listen(tab, 'keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next !== undefined) { event.preventDefault(); activate(tabs[next]); tabs[next].focus(); }
      });
    });
    qa('[data-product-tab-link]').forEach((link) => listen(link, 'click', (event) => {
      event.preventDefault();
      if (root.dataset.soldOut === 'true') return;
      activate(tabs.find((tab) => tab.dataset.productTab === link.dataset.productTabLink), true);
    }));
    qa('.tsyrk-product__tab-panel table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'tsyrk-product__table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', 'Product information table');
      table.before(wrapper);
      wrapper.append(table);
    });
    // Imported rich text can end in empty paragraphs or differently spaced
    // wrappers. Normalize only its trailing edge, not the spacing within it.
    const markContentEnd = (container) => {
      let last = container.lastChild;
      while (last?.nodeType === Node.TEXT_NODE && !last.textContent.trim()) last = last.previousSibling;
      // Do not walk backwards through <br> elements past a final text node:
      // shift+enter line breaks are meaningful content, not empty wrappers.
      while (last?.nodeType === Node.ELEMENT_NODE && !last.textContent.trim() && !last.querySelector('img, picture, video, iframe, svg, table, hr, br') && !last.matches('img, picture, video, iframe, svg, table, hr, br')) {
        last.hidden = true;
        last = last.previousSibling;
        while (last?.nodeType === Node.TEXT_NODE && !last.textContent.trim()) last = last.previousSibling;
      }
      if (last?.nodeType === Node.ELEMENT_NODE && !last.matches('br')) {
        last.classList.add('is-last-content');
        markContentEnd(last);
      }
    };
    qa('.tsyrk-product__tab-panel').forEach(markContentEnd);
    listen(q('.tsyrk-product__form form'), 'submit', async (event) => {
      event.preventDefault();
      if (busy) return;
      if (size && !selectedSize) { openSize(true); sizes.find((button) => !button.disabled)?.focus(); return; }
      const variant = selectedVariant();
      if (!variant?.available) return;
      busy = true;
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      error.hidden = true;
      try {
        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }), signal: controller.signal
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');
        q('[data-product-checkout]').hidden = false;
        root.classList.add('is-added');
        q('[data-product-status]').textContent = 'Added to your bag.';
        const cartResponse = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { signal: controller.signal }).catch(() => null);
        const cart = cartResponse?.ok ? await cartResponse.json().catch(() => null) : null;
        if (cart) {
          document.querySelectorAll('[data-tsyrk-bag-count]').forEach((bag) => { bag.textContent = bag.textContent.replace(/\s+\d+$/, '') + ' ' + cart.item_count; });
        }
      } catch (failure) {
        if (failure.name !== 'AbortError') { error.textContent = failure.message; error.hidden = false; }
      } finally {
        busy = false;
        add.removeAttribute('aria-busy');
        sync();
        dock();
      }
    });
    const track = q('[data-gallery-track]');
    const prev = q('[data-gallery-prev]');
    const next = q('[data-gallery-next]');
    const controls = q('[data-gallery-controls]');
    const galleryState = () => {
      if (!track) return;
      const overflow = track.scrollWidth - track.clientWidth;
      controls.hidden = overflow < 2;
      prev.disabled = track.scrollLeft < 2;
      next.disabled = track.scrollLeft >= overflow - 2;
    };
    const step = (direction) => {
      const photo = [...track.children].find((item) => item.offsetWidth);
      track.scrollBy({ left: direction * ((photo?.offsetWidth || track.clientWidth) + 32), behavior: motion() });
    };
    listen(prev, 'click', () => step(-1));
    listen(next, 'click', () => step(1));
    listen(track, 'scroll', galleryState, { passive: true });
    const dialog = q('[data-product-lightbox]');
    const fullImage = q('[data-lightbox-image]');
    let photoIndex = 0;
    let opener;
    const showPhoto = (index) => {
      photoIndex = Math.max(0, Math.min(photos.length - 1, index));
      if (!photos[photoIndex]) return;
      fullImage.src = photos[photoIndex].src;
      fullImage.alt = photos[photoIndex].alt;
      dialog.style.setProperty('--lightbox-photo-ratio', photos[photoIndex].ratio || (2 / 3));
      q('[data-lightbox-count]').textContent = (photoIndex + 1) + ' / ' + photos.length;
      q('[data-lightbox-prev]').disabled = photoIndex === 0;
      q('[data-lightbox-next]').disabled = photoIndex === photos.length - 1;
    };
    qa('[data-gallery-index]').forEach((button) => listen(button, 'click', () => {
      opener = button;
      showPhoto(Number(button.dataset.galleryIndex));
      dialog.showModal();
      document.documentElement.classList.add('tsyrk-gallery-open');
      q('[data-lightbox-close]').focus();
    }));
    listen(q('[data-lightbox-close]'), 'click', () => dialog.close());
    listen(dialog, 'close', () => { document.documentElement.classList.remove('tsyrk-gallery-open'); opener?.focus({ preventScroll: true }); });
    listen(q('[data-lightbox-prev]'), 'click', () => showPhoto(photoIndex - 1));
    listen(q('[data-lightbox-next]'), 'click', () => showPhoto(photoIndex + 1));
    listen(dialog, 'keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); showPhoto(photoIndex + (event.key === 'ArrowRight' ? 1 : -1)); }
    });
    let touchX;
    listen(fullImage, 'touchstart', (event) => { touchX = event.changedTouches[0].clientX; }, { passive: true });
    listen(fullImage, 'touchend', (event) => {
      const distance = event.changedTouches[0].clientX - touchX;
      if (Math.abs(distance) > 50) showPhoto(photoIndex + (distance < 0 ? 1 : -1));
    }, { passive: true });
    const slot = q('[data-purchase-slot]');
    const purchase = q('[data-purchase-inner]');
    const footer = document.querySelector('.tsyrk-v2-footer');
    let frame = 0;
    const dock = () => {
      frame = 0;
      if (!purchase || !slot) return;
      const rect = slot.getBoundingClientRect();
      const docked = purchase.classList.contains('is-docked');
      const style = getComputedStyle(purchase);
      const naturalHeight = purchase.offsetHeight - (docked ? parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) : 0);
      // Use the virtual, uncollapsed block edge as the threshold. Keeping its
      // old height in the grid left a large blank space above the tabs.
      if (innerWidth > 1024 || rect.top + naturalHeight > innerHeight) {
        purchase.classList.remove('is-docked');
        root.classList.remove('is-purchase-docked');
        slot.style.minHeight = '';
        root.style.setProperty('--purchase-clearance', '0px');
        return;
      }
      slot.style.minHeight = '';
      purchase.classList.add('is-docked');
      root.classList.add('is-purchase-docked');
      purchase.style.setProperty('--purchase-left', rect.left + 'px');
      purchase.style.setProperty('--purchase-width', rect.width + 'px');
      const gap = parseFloat(getComputedStyle(root).getPropertyValue('--purchase-content-gap')) || 48;
      const footerGap = 72;
      const topPadding = parseFloat(getComputedStyle(purchase).paddingTop) || 0;
      const bottomPadding = parseFloat(getComputedStyle(purchase).paddingBottom) || 0;
      const footerInset = footer ? parseFloat(getComputedStyle(footer).paddingTop) || 0 : 0;
      // Measure the 72px to the visible footer content, excluding its own padding
      // and the dock's safe-area padding. The tab-to-button gap is always 48px.
      root.style.setProperty('--purchase-clearance', (purchase.offsetHeight + gap + footerGap - topPadding - bottomPadding - footerInset) + 'px');
      purchase.style.setProperty('--purchase-lift', Math.min(0, (footer?.getBoundingClientRect().top ?? innerHeight + footerGap) + footerInset - innerHeight - footerGap + bottomPadding) + 'px');
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(dock); };
    listen(window, 'scroll', schedule, { passive: true });
    listen(window, 'resize', () => { galleryState(); schedule(); }, { passive: true });
    const observer = new ResizeObserver(() => { galleryState(); schedule(); });
    if (track) observer.observe(track);
    if (purchase) observer.observe(purchase);
    observer.observe(root);
    root.cleanupProduct = () => { controller.abort(); observer.disconnect(); cancelAnimationFrame(frame); document.documentElement.classList.remove('tsyrk-gallery-open'); };
    sync();
    galleryState();
    dock();
  };
  const prepare = (scope = document) => scope.querySelectorAll('[data-tsyrk-product]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepare()); else prepare();
  document.addEventListener('shopify:section:load', (event) => prepare(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-tsyrk-product]').forEach((root) => root.cleanupProduct?.()));
})();
